import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Pagination from './ui/Pagination';
import { FaSearch, FaSort, FaAndroid, FaTrash } from 'react-icons/fa';
import LoadingSpinner from '../utils/LoadingSpinner';
import '../styles/Reports.css';
import '../styles/DeviceModal.css';
import './../styles/messageContainer.css';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useSite } from './../context/SiteContext';
import deviceService from '../services/DeviceService';
import Button from './ui/Button';
import './../styles/InformationSection.css';
import useConfirmation from './../hooks/useConfirmation';

const EMPTY_DATE_VALUES = new Set(['', 'null', 'undefined']);
const isDefaultEmptyDate = (value) =>
  /^0001-01-01T00:00:00(?:\.0+)?(?:Z)?$/i.test(value);
const CREATED_DATE_FIELDS = [
  'createdDate',
];

const getFirstMatchingValue = (item, fieldNames) => {
  for (const field of fieldNames) {
    if (Object.prototype.hasOwnProperty.call(item, field)) {
      return item[field];
    }
  }
  return null;
};

const parseOptionalDate = (value) => {
  if (value == null) return null;

  const normalized = String(value).trim();
  if (
    !normalized ||
    EMPTY_DATE_VALUES.has(normalized.toLowerCase()) ||
    isDefaultEmptyDate(normalized)
  ) {
    return null;
  }

  const parsed = new Date(normalized);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatDisplayDate = (value) => {
  if (!(value instanceof Date) || Number.isNaN(value.getTime())) {
    return '—';
  }

  return value.toLocaleDateString('en-GB', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
};

const normalizePendingActivationRecord = (item, index) => {
  const username = item.username || item.userName || item.visorId || `record-${index}`;
  const createdDate = parseOptionalDate(getFirstMatchingValue(item, CREATED_DATE_FIELDS));

  return {
    ...item,
    recordKey: String(item.id ?? username ?? index),
    username,
    createdDate,
    canDelete: true,
    statusText: 'Pending activation',
  };
};

const PendingActivationTable = () => {
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(100);
  const [totalPages, setTotalPages] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: 'username', direction: 'asc' });
  const [error, setError] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const [isDeleting, setIsDeleting] = useState(false);
  const [actionMessage, setActionMessage] = useState(null);
  const { selectedSiteId } = useSite();
  const { showConfirmation, ConfirmationBox } = useConfirmation();

  const fetchUsers = useCallback(async ({ preserveActionMessage = false } = {}) => {
    setIsLoading(true);
    setError(null);
    if (!preserveActionMessage) {
      setActionMessage(null);
    }

    try {
      const response = await deviceService.getVisorIdListMobile({ groupId: selectedSiteId });
      const data = Array.isArray(response) ? response : [];

      setUsers(data);
      setSelectedRows([]);

      if (data.length > 0) {
        const totalRecords = data[0].totalDevices || data.length;
        const calculatedPages = Math.ceil(totalRecords / pageSize);
        setTotalPages(calculatedPages || 1);
      } else {
        setTotalPages(1);
        setError('No records found');
      }
    } catch (err) {
      setError('No records found.');
    } finally {
      setIsLoading(false);
    }
  }, [pageSize, selectedSiteId]);

  useEffect(() => {
    setCurrentPage(1);
    setTotalPages(1);
    setUsers([]);
    setError(null);
    setSearchQuery('');
    setSelectedRows([]);
    setActionMessage(null);

    if (selectedSiteId) {
      fetchUsers();
    }
  }, [fetchUsers, selectedSiteId]);

  const handleSort = (columnKey) => {
    const isSameColumn = sortConfig.key === columnKey;
    const direction =
      isSameColumn && sortConfig.direction === 'asc' ? 'desc' : 'asc';
    setSortConfig({ key: columnKey, direction });
  };

  const renderSortArrow = (columnKey) => {
    if (sortConfig.key === columnKey) {
      return sortConfig.direction === 'asc' ? ' ▲' : ' ▼';
    }
    return <FaSort className="reports-font-style-grey" />;
  };

  const handlePageChange = (newPage) => {
    if (newPage < 1) newPage = 1;
    if (newPage > totalPages) newPage = totalPages;
    setCurrentPage(newPage);
  };

  const normalizedUsers = useMemo(
    () => users.map((item, index) => normalizePendingActivationRecord(item, index)),
    [users],
  );

  const filteredUsers = normalizedUsers.filter((u) =>
    (u.username || '')
      .toString()
      .toLowerCase()
      .includes(searchQuery.toLowerCase())
  );

  const sortedUsers = [...filteredUsers].sort((a, b) => {
    const { key, direction } = sortConfig;
    const dir = direction === 'asc' ? 1 : -1;

    if (key === 'createdDate') {
      const timeA = a[key] instanceof Date ? a[key].getTime() : -Infinity;
      const timeB = b[key] instanceof Date ? b[key].getTime() : -Infinity;
      if (timeA < timeB) return -1 * dir;
      if (timeA > timeB) return 1 * dir;
      return 0;
    }

    const valA = (a[key] ?? '').toString().toLowerCase();
    const valB = (b[key] ?? '').toString().toLowerCase();
    if (valA < valB) return -1 * dir;
    if (valA > valB) return 1 * dir;
    return 0;
  });

  const exportToXlsx = async () => {
    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Users');

      worksheet.columns = [
        { header: 'Device ID', key: 'username', width: 30 },
        { header: 'Created Date', key: 'createdDate', width: 20 },
        { header: 'Status', key: 'statusText', width: 24 },
      ];

      worksheet.getRow(1).font = { bold: true };
      worksheet.getRow(1).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFE0E0E0' },
      };

      const data = sortedUsers.map((u) => ({
        username: u.username || '—',
        createdDate: formatDisplayDate(u.createdDate),
        statusText: u.statusText,
      }));

      data.forEach((row) => worksheet.addRow(row));

      const buffer = await workbook.xlsx.writeBuffer();
      const stamp = new Date().toISOString().slice(0, 10);
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      saveAs(blob, `pending_activation_users_${stamp}.xlsx`);
    } catch (exportError) {
      console.error('Error exporting to Excel:', exportError);  
      alert('Failed to export Excel file. Please try again.');
    }
  };

  const startIndex = (currentPage - 1) * pageSize;
  const pagedUsers = sortedUsers.slice(startIndex, startIndex + pageSize);
  const selectedEligibleCount = selectedRows.filter((recordKey) =>
    normalizedUsers.some((item) => item.recordKey === recordKey && item.canDelete),
  ).length;

  const toggleRowSelection = (recordKey) => {
    setSelectedRows((prev) =>
      prev.includes(recordKey)
        ? prev.filter((key) => key !== recordKey)
        : [...prev, recordKey],
    );
  };

  const deletePendingActivationRecords = async (recordsToDelete) => {
    setIsDeleting(true);
    setActionMessage(null);

    //I need to put the actual endpoint here for deleting a nominal 
    try {
      const results = await Promise.allSettled(
        recordsToDelete.map((item) => deviceService.deleteVisor(item.username)),
      );

      const failedResults = results.filter((result) => result.status === 'rejected');
      const successCount = results.length - failedResults.length;

      if (successCount > 0) {
        setUsers((prev) =>
          prev.filter((item, index) => {
            const normalized = normalizePendingActivationRecord(item, index);
            return !recordsToDelete.some((selected) => selected.recordKey === normalized.recordKey);
          }),
        );
        setSelectedRows((prev) =>
          prev.filter((recordKey) => !recordsToDelete.some((item) => item.recordKey === recordKey)),
        );
      }

      if (failedResults.length > 0) {
        const staleRecordCount = failedResults.filter(
          (result) => result.reason?.status === 404,
        ).length;
        await fetchUsers({ preserveActionMessage: true });
        const refreshSuffix = staleRecordCount > 0
          ? ' Some of the selected records were no longer available, so the list has been refreshed.'
          : ' The list has been refreshed and now shows the latest available records.';
        setActionMessage({
          type: 'error',
          text: successCount > 0
            ? `${successCount} record${successCount === 1 ? '' : 's'} ${successCount === 1 ? 'was' : 'were'} deleted. We could not remove ${failedResults.length} other ${failedResults.length === 1 ? 'record' : 'records'}. ${refreshSuffix}`
            : `We could not remove the selected record${recordsToDelete.length === 1 ? '' : 's'}. ${refreshSuffix}`,
        });
      } else {
        setActionMessage({
          type: 'success',
          text: `${successCount} record${successCount === 1 ? '' : 's'} deleted successfully.`,
        });
      }
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteSelected = () => {
    const recordsToDelete = normalizedUsers.filter(
      (item) => item.canDelete && selectedRows.includes(item.recordKey),
    );

    if (recordsToDelete.length === 0) {
      setActionMessage({
        type: 'error',
        text: 'Select at least one record to delete.',
      });
      return;
    }

    const recordLabel = `record${recordsToDelete.length === 1 ? '' : 's'}`;

    showConfirmation(
      `Delete ${recordsToDelete.length} pending activation ${recordLabel} permanently?`,
      [
        {
          label: 'Delete',
          className: 'btn btn-danger confirm-button',
          action: () => deletePendingActivationRecords(recordsToDelete),
        },
      ],
      () => {},
    );
  };

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(sortedUsers.length / pageSize));
    if (currentPage > maxPage) {
      setCurrentPage(maxPage);
    }
    setTotalPages(maxPage);
  }, [currentPage, pageSize, sortedUsers.length]);

  return (
    <div>
      <div className="user-report-advanced-search">
        <div className="report-search-group">
          <div className="report-search-input-group">
            <span className="report-search-input-group-text">
              <FaSearch />
            </span>
            <input
              type="text"
              placeholder="Search users"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
              className="report-search-form-control"
              autoComplete="off"
            />
          </div>
          <Button
            onClick={() => {
              setCurrentPage(1);
            }}
          >
            Search
          </Button>
          <Button onClick={() => fetchUsers()}>
            Show All
          </Button>
          <Button
            buttonStyle="btn-danger"
            onClick={handleDeleteSelected}
            disabled={selectedEligibleCount === 0 || isDeleting}
          >
            <FaTrash className="pending-activation-delete-icon" />
            {isDeleting ? 'Deleting…' : `Delete Selected (${selectedEligibleCount})`}
          </Button>
          <Button
            buttonStyle="btn-alternate"
            onClick={exportToXlsx}
            disabled={sortedUsers.length === 0}
          >
            Export XLSX
          </Button>
        </div>
      </div>

      {actionMessage ? (
        <div
          className={`pending-activation-feedback ${
            actionMessage.type === 'error' ? 'is-error' : 'is-success'
          }`}
        >
          {actionMessage.text}
        </div>
      ) : null}

      <div className="transfer-table-custom-container pagination-container">
        <div className="report-table-section">
          {isLoading && <LoadingSpinner />}
          {!isLoading && error ? (
            <div className="message-container">
              <FontAwesomeIcon
                icon={faTriangleExclamation}
                size="3x"
                className="message-icon"
              />
              <p className="message-text">
                {error || 'No users found.'}
              </p>
            </div>
          ) : (
            <div className="report-table-container">
              <table className="report-table">
                <thead>
                  <tr>
                    <th className="pending-activation-checkbox-column" />
                    <th onClick={() => handleSort('username')}>
                      Device ID {renderSortArrow('username')}
                    </th>
                    <th onClick={() => handleSort('createdDate')}>
                      Created {renderSortArrow('createdDate')}
                    </th>
                    <th onClick={() => handleSort('statusText')}>
                      Status {renderSortArrow('statusText')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {pagedUsers.map((item) => (
                    <tr key={item.recordKey}>
                      <td className="pending-activation-checkbox-cell">
                        <label className="pending-activation-checkbox-label">
                          <input
                            type="checkbox"
                            checked={selectedRows.includes(item.recordKey)}
                            onChange={() => toggleRowSelection(item.recordKey)}
                            aria-label={`Select ${item.username}`}
                          />
                          <span className="pending-activation-checkmark" />
                        </label>
                      </td>
                      <td>
                        <FaAndroid className="reports-font-style-grey" />
                        {item.username}
                      </td>
                      <td>{formatDisplayDate(item.createdDate)}</td>
                      <td>{item.statusText}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          totalRecords={sortedUsers.length}
          docked
        />
      </div>
      <ConfirmationBox />
    </div>
  );
};

export default PendingActivationTable;
